import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { ScoreGauge } from "../../components/ScoreGauge";
import { scoreColor } from "../../lib/scoring";
import {
  Target, Sparkles, TrendingUp, TrendingDown, Minus, ArrowRight, GraduationCap,
  Award, Trophy, Lock, CheckCircle2, Circle, Zap, BarChart3, Star, Rocket, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

const CAT = {
  critical: { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
  high: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  medium: { bg: "#E5EEFF", text: "#1E40AF", dot: "#0055FF" },
  strong: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
  excellent: { bg: "#EDE9FE", text: "#5B21B6", dot: "#8B5CF6" },
};
const EXPERIENCE = ["Fresher", "Experienced"];

export default function ReadinessProfile() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get("/candidate/intelligence").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  if (!d) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const p = d.profile;

  if (!d.has_data) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="h-16 w-16 rounded-2xl bg-brand-light grid place-items-center mx-auto mb-4"><Rocket className="h-7 w-7 text-brand" /></div>
        <h1 className="font-heading text-2xl font-bold text-ink">Build your Readiness Profile</h1>
        <p className="text-ink-soft text-sm mt-2 mb-5">Complete your first interview to unlock skill-gap analysis, a personalized improvement roadmap and course recommendations.</p>
        <button onClick={() => navigate("/candidate/book")} className="text-sm font-bold text-white bg-brand px-6 py-3 rounded-full shadow-ambient">Start Interview</button>
      </div>
    );
  }

  const setExp = async (lvl) => {
    await api.put("/candidate/experience", { experience_level: lvl });
    toast.success(`Experience set to ${lvl}`);
    load();
  };

  const toggle = async (t) => {
    await api.post("/candidate/action-center/toggle", { task_id: t.id, completed: !t.completed });
    load();
  };

  const TrendIcon = p.trend.includes("↑") ? TrendingUp : p.trend.includes("↓") ? TrendingDown : Minus;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Career Readiness</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Einstein Score Profile</h1>
        <p className="text-ink-soft text-sm mt-1">Your persistent employability profile — updated after every interview.</p>
      </div>

      {/* Profile + Next action */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 flex flex-col items-center text-center">
          <ScoreGauge value={p.readiness} size={170} />
          <div className="mt-3 font-heading font-bold text-ink">{p.target_role}</div>
          <div className="flex items-center gap-1.5 text-xs font-semibold mt-1" style={{ color: p.trend.includes("↓") ? "#EF4444" : "#10B981" }}>
            <TrendIcon className="h-3.5 w-3.5" /> {p.trend} · {p.interview_count} interviews
          </div>
          <div className="mt-4 w-full flex gap-1 bg-slate-100 rounded-full p-1">
            {EXPERIENCE.map((e) => (
              <button key={e} data-testid={`exp-${e}`} onClick={() => setExp(e)}
                className={`flex-1 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${d.experience_level === e ? "bg-white text-ink shadow-sm" : "text-ink-soft"}`}>{e}</button>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 bg-slate-900 text-white border-slate-900">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-300 mb-2"><Zap className="h-4 w-4" /> Your Next Best Action</div>
            <div className="font-heading text-xl font-bold">{d.next_action.title}</div>
            <p className="text-slate-300 text-sm mt-1">{d.next_action.detail}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button data-testid="next-action-cta" onClick={() => navigate("/candidate/book")} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full transition-colors">
                Take Another Interview <ArrowRight className="h-4 w-4" />
              </button>
              {d.next_action.expected_gain > 0 && <span className="text-xs num font-bold text-emerald-300">Expected +{d.next_action.expected_gain} readiness pts</span>}
            </div>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-3 flex items-center gap-2"><Star className="h-3.5 w-3.5 text-amber-400" /> Strengths</div>
              <div className="flex flex-wrap gap-2">{p.strengths.length ? p.strengths.map((s) => <span key={s} className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700">{s}</span>) : <span className="text-sm text-ink-soft">Building up…</span>}</div>
            </Card>
            <Card className="p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-3 flex items-center gap-2"><Target className="h-3.5 w-3.5 text-red-500" /> Improvement Areas</div>
              <div className="flex flex-wrap gap-2">{p.improvement_areas.length ? p.improvement_areas.map((s) => <span key={s} className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-50 text-red-700">{s}</span>) : <span className="text-sm text-ink-soft">None — great work!</span>}</div>
            </Card>
          </div>
        </div>
      </div>

      {/* Skill Gap Engine */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-brand" /> Skill Gap Analysis</div>
        <div className="space-y-4">
          {d.gaps.map((g) => {
            const c = CAT[g.category_key];
            return (
              <div key={g.skill} data-testid={`gap-${g.skill}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: c.dot }} />
                    <span className="font-semibold text-ink text-sm">{g.label}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.text }}>{g.category}</span>
                  </div>
                  <span className="num text-xs"><span className="text-ink-faint">{g.current} → {g.target}</span> <span className="font-bold" style={{ color: g.gap > 0 ? "#EF4444" : "#10B981" }}>{g.gap > 0 ? `-${g.gap}` : `+${-g.gap}`}</span></span>
                </div>
                <div className="relative h-2.5 rounded-full bg-slate-100">
                  <div className="absolute h-full rounded-full" style={{ width: `${g.current}%`, background: scoreColor(g.current) }} />
                  <div className="absolute top-1/2 -translate-y-1/2 h-4 w-0.5 bg-slate-900" style={{ left: `${g.target}%` }} title="Target" />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Recommendations + Roadmap */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand" /> Personalized Recommendations</div>
          <div className="space-y-3">
            {d.recommendations.map((r, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`rec-${i}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold text-ink">{r.label}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-ink-soft">{r.category}</span>
                </div>
                <p className="text-sm text-ink-soft leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-bold text-ink">Improvement Roadmap</div>
            <span className="text-xs num font-bold text-ink-soft">{d.roadmap.current_readiness} → {d.roadmap.target}+ target</span>
          </div>
          <div className="space-y-3">
            {d.roadmap.weeks.map((w, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="h-8 w-8 rounded-full bg-brand-light grid place-items-center text-xs font-bold text-brand num">{i + 1}</span>
                  {i < d.roadmap.weeks.length - 1 && <span className="w-px flex-1 bg-slate-200 my-1" />}
                </div>
                <div className="pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-ink text-sm">{w.week}</span>
                    <span className="text-[10px] num text-ink-faint">Day {w.days}</span>
                  </div>
                  <div className="text-brand text-sm font-semibold">{w.focus}</div>
                  <ul className="mt-1 space-y-0.5">{w.tasks.map((t, j) => <li key={j} className="text-xs text-ink-soft">· {t}</li>)}</ul>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Before vs After + Role comparison */}
      <div className="grid lg:grid-cols-2 gap-6">
        {d.before_after ? (
          <Card className="p-6">
            <div className="font-heading font-bold text-ink mb-4">Before vs After</div>
            <div className="flex items-center justify-center gap-6 mb-5">
              <div className="text-center"><div className="text-xs text-ink-faint">Previous</div><div className="num font-bold text-3xl text-ink-soft">{d.before_after.previous}</div></div>
              <ArrowRight className="h-5 w-5 text-ink-faint" />
              <div className="text-center"><div className="text-xs text-ink-faint">Current</div><div className="num font-bold text-3xl text-ink">{d.before_after.current}</div></div>
              <div className="text-center"><div className="text-xs text-ink-faint">Change</div><div className="num font-bold text-3xl" style={{ color: d.before_after.improvement >= 0 ? "#10B981" : "#EF4444" }}>{d.before_after.improvement >= 0 ? "+" : ""}{d.before_after.improvement}</div></div>
            </div>
            <div className="space-y-2">
              {d.before_after.deltas.map((x) => (
                <div key={x.skill} className="flex items-center justify-between text-sm">
                  <span className="text-ink-soft">{x.label}</span>
                  <span className="num font-bold" style={{ color: x.delta > 0 ? "#10B981" : x.delta < 0 ? "#EF4444" : "#94A3B8" }}>{x.delta > 0 ? "+" : ""}{x.delta}</span>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="p-6 grid place-items-center text-center">
            <div><div className="font-heading font-bold text-ink">Before vs After</div><p className="text-sm text-ink-soft mt-2">Complete a second interview to see your improvement comparison.</p></div>
          </Card>
        )}

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-1">Job Role Comparison</div>
          <p className="text-xs text-ink-soft mb-4">Discover roles where you're most prepared.</p>
          <div className="space-y-3">
            {d.role_comparison.map((r) => (
              <div key={r.role_id} data-testid={`role-cmp-${r.role_id}`}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium text-ink">{r.name}</span><span className="num font-bold text-ink">{r.readiness}%</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.readiness}%`, background: scoreColor(r.readiness) }} /></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Action Center + Gamification */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-bold text-ink flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" /> Action Center</div>
            <span className="text-xs num font-bold text-brand">{d.action_center.completed} of {d.action_center.total} completed</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(d.action_center.completed / d.action_center.total) * 100}%` }} /></div>
          <div className="space-y-2">
            {d.action_center.tasks.map((t) => (
              <button key={t.id} data-testid={`action-${t.id}`} onClick={() => toggle(t)}
                className={`w-full flex items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${t.completed ? "border-emerald-200 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                {t.completed ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                <span className={`text-sm font-semibold ${t.completed ? "text-emerald-700 line-through" : "text-ink"}`}>{t.title}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Achievements</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {d.gamification.earned.map((b) => (
              <div key={b.name} className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-center" data-testid={`badge-${b.name}`}>
                <Award className="h-6 w-6 text-amber-500 mx-auto mb-1.5" />
                <div className="text-xs font-bold text-ink leading-tight">{b.name}</div>
              </div>
            ))}
            {d.gamification.locked.map((b) => (
              <div key={b.name} className="rounded-xl border border-slate-200 p-3.5 text-center opacity-60">
                <Lock className="h-6 w-6 text-slate-300 mx-auto mb-1.5" />
                <div className="text-xs font-bold text-ink-soft leading-tight">{b.name}</div>
                <div className="text-[10px] num text-ink-faint mt-0.5">{b.progress}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Courses */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-1 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-brand" /> Recommended For You</div>
        <p className="text-xs text-ink-soft mb-4">Courses mapped to your skill gaps.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {d.courses.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-5 hover:border-brand hover:shadow-card transition-all" data-testid={`course-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-ink-soft">{c.provider}</span>
                <span className="text-xs font-bold text-amber-500 flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400" /> {c.rating}</span>
              </div>
              <div className="font-semibold text-ink text-sm leading-tight">{c.title}</div>
              <div className="text-xs text-brand font-semibold mt-1">Addresses: {c.skill_label}</div>
              <p className="text-xs text-ink-soft mt-2">{c.why}</p>
              <div className="text-[11px] num font-bold text-emerald-600 mt-2">Expected benefit: {c.expected_benefit}</div>
              <button className="mt-3 w-full text-xs font-bold text-brand hover:bg-brand-light px-4 py-2 rounded-full transition-colors">Start Learning</button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
